import { Formik } from 'formik';
import Fuse from 'fuse.js';
import _ from 'lodash';
import pluralize from 'pluralize';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sortAlphabetically } from '~/utils';
import * as Yup from 'yup';
import { FieldControl, FixedSingleSelect, Form } from '~/components';
import BulkActionModal from '~/components/bulk-action/BulkActionModal';
import { useApi, useWorkspace } from '~/contexts';
import opportunityStageCategories from '~/lookups/opportunity-stage-categories';
import OpportunityDetails from './OpportunityDetails';

function getStatusName(stage) {
  return opportunityStageCategories[stage.statusId]?.name ?? stage.statusId;
}

function getStageOptionLabel(stage) {
  return `${getStatusName(stage)} - ${stage.name} (${stage.probability}%)`;
}

function getSelectedStageLabel(stage, showPipelineName) {
  if (!stage) return '';
  if (showPipelineName) {
    return `${stage.opportunityPipeline?.name} - ${stage.name}`;
  }

  return stage.name;
}

export default function BulkSetStage({ ids, opportunityPipelineIds = [], onClose, onComplete }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const formRef = useRef();
  const [data, setData] = useState([]);
  const [filterValue, setFilterValue] = useState('');
  const [isReady, setIsReady] = useState(false);

  const scopedOpportunityPipelineIds = useMemo(
    () => _.sortBy(_.uniq(opportunityPipelineIds.filter(Boolean))),
    [opportunityPipelineIds],
  );
  const stagesById = useMemo(() => _.keyBy(data, 'id'), [data]);

  useEffect(() => {
    let isMounted = true;

    setData([]);
    setFilterValue('');
    setIsReady(false);

    (async () => {
      const result = await api.www
        .workspaces(workspace.id)
        .opportunityStages()
        .get({
          isActive: true,
          opportunityPipelineId: scopedOpportunityPipelineIds.length ? scopedOpportunityPipelineIds : undefined,
        });

      if (!isMounted) return;

      setData(result.data);
      setIsReady(true);
    })();

    return () => {
      isMounted = false;
    };
  }, [api, scopedOpportunityPipelineIds, workspace.id]);

  const filteredStages = useMemo(() => {
    if (!filterValue) return data;

    return new Fuse(data, {
      keys: ['name', 'opportunityPipeline.name', 'statusId'],
      threshold: 0.4,
      getFn: (stage, path) => {
        if (path === 'statusId') {
          return getStatusName(stage);
        }

        return _.get(stage, path);
      },
    })
      .search(filterValue)
      .map((result) => result.item);
  }, [data, filterValue]);

  const groups = useMemo(() => {
    return sortAlphabetically(
      _(filteredStages)
        .groupBy((option) => option.opportunityPipelineId)
        .map((pipelineStages) => {
          const pipeline = pipelineStages[0]?.opportunityPipeline;

          return {
            pipeline,
            stages: sortAlphabetically(pipelineStages, ['probability', 'name']),
          };
        })
        .filter((group) => group.pipeline)
        .value(),
      'pipeline.name',
    );
  }, [filteredStages]);

  return (
    <BulkActionModal
      title="Bulk Set Stage"
      onClose={onClose}
      onComplete={onComplete}
      onConfirm={() => formRef.current?.submitForm()}
      renderRow={OpportunityDetails}>
      <Formik
        innerRef={formRef}
        initialValues={{ opportunityStageId: null }}
        onSubmit={async (values) => {
          const body = { opportunityStageId: values.opportunityStageId };
          return api.www
            .workspaces(workspace.id)
            .opportunities()
            .bulkSetStage(ids, body)
            .then((r) => r.data);
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          opportunityStageId: Yup.string().label('Opportunity Stage').nullable().required(),
        })}>
        {(formik) => (
          <Form>
            <p>
              Set the stage of {ids.length} {pluralize('opportunity', ids.length)} to:
            </p>

            <Form.Control>
              <FieldControl
                error={
                  (formik.touched.opportunityStageId || formik.submitCount > 0) && formik.errors.opportunityStageId
                }>
                <FixedSingleSelect
                  name="opportunityStageId"
                  placeholder="Opportunity Stage"
                  materialPlaceholder={false}
                  showFilter
                  filterPlaceholder="Search stages"
                  disabled={!isReady}
                  value={formik.values.opportunityStageId || ''}
                  valueRenderer={getSelectedStageLabel(
                    stagesById[formik.values.opportunityStageId],
                    scopedOpportunityPipelineIds.length > 1,
                  )}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  onFilterChange={(event) => setFilterValue(event.target.value)}>
                  {groups.map(({ pipeline, stages }) => (
                    <optgroup key={pipeline.id} label={pipeline.name}>
                      {stages.map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {getStageOptionLabel(stage)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </FixedSingleSelect>
              </FieldControl>
            </Form.Control>
          </Form>
        )}
      </Formik>
    </BulkActionModal>
  );
}
